import _ from 'underscore';
import React, { useEffect, useCallback } from 'react';
import classNames from 'classnames';
import { makeStyles } from '@fluentui/react-components';
import PropTypes from 'prop-types';
import { fetchConversionTracking, getConversionGoalsFilterStore } from '@bingads-webui-campaign/overview-cards-common';
import { withDelayedStyles } from '@bingads-webui-campaign-react/common-utils';
import { getConversionTrackingCardStyle, metricPickerFluentV2Style } from '@bingads-webui-campaign-react/overview-card-styles';
import { useHeaderStateContext } from '@bingads-webui-campaign-react/overview-card';

import ConversionTrackingCardReact from './conversion-tracking-card-react/conversion-tracking-card';

// Most of the content styles are from existing conversion tracking card
const getDefaultStyles = (_, { palette = {} }) => ({
  '@global': Object.assign(
    getConversionTrackingCardStyle(palette),
    metricPickerFluentV2Style
  ),
});

export const useStyles = makeStyles({
  cardContent: {
    // to make sure empty state flex container can take full height from parent
    // flex element can take 100% height from parent only if parent has fixed height
    height: '307px',
    '& .conversion-tracking-state': {
      // to make the container height fixed with 377px, this is from Figma.
      height: '307px',

      '& .carousel-footer': {
        paddingBottom: '0px !important',
      },
    },

    '& .conversion-empty-state': {
      paddingBottom: '0px !important',
    },

    // TODO: [yaoyao] add shimmer for loading status
    // https://msasg.visualstudio.com/Bing_Ads/_workitems/edit/7173186
    '& .conversion-tracking-card-data-loading': {
      height: '307px',
    },
  },
  smallSizeCard: {
    '& .pop-range': {
      display: 'none',
    },
  },
});

const conversionTileFetch = ({
  entitySet,
  filter,
  campaignType,
  jsdata,
  getCardsConfig,
  dateUtil,
  permissions,
  disableCache,
  cacheManager,
  // 1 for expert mode, we don't expect to have smart mode account anymore
  accountsMode = 1,
  typeId,
}) => {
  const fetch = ({
    dateRange,
    metric,
    selectedMenuItem,
    fetchSingleTile = false,
  }) => fetchConversionTracking({
    dateRange,
    selectedMenuItem,
    metric,
    entitySet,
    filter,
    campaignType,
    jsdata,
    getCardsConfig,
    dateUtil,
    fetchSingleTile,
    permissions,
    disableCache,
    cacheManager,
    accountsMode,
    dfmCacheKey: typeId,
  });

  return fetch;
};

function ConversionTrackingCardFluentInternal({
  getCardsConfig,
  i18n,
  permissions,
  preferenceService,
  deps,
  jsdata,
  dateUtil,
  dateRange,
  filter,
  campaignType,
  constructOptions,
  cacheManager,
  legacyI18n,
  cardTypeId,
  perfMarkerUtils,
  responsiveSize,
}) {
  const conversionGoalsFilterStore = getConversionGoalsFilterStore({ preferenceService });
  const {
    entitySet,
  } = constructOptions();
  const classes = useStyles();
  const headerState = useHeaderStateContext();
  const viewRef = React.createRef();
  const {
    willFetchData,
    willRender,
    done,
  } = perfMarkerUtils;
  // For size larger than l, we split into columns, so we don't show range column
  // For size smaller than l, we don't have enough space to show range column
  const hideRangeColumn = responsiveSize !== 'l';

  useEffect(() => {
    if (viewRef.current && headerState && headerState.selectedItemValue) {
      viewRef.current.handleUpdate({
        selectedMenuItem: headerState.selectedItemValue,
      });
    }
  }, [headerState, viewRef]);

  useEffect(() => {
    if (viewRef.current && dateRange) {
      viewRef.current.handleUpdate({
        dateRange,
      });
    }
  }, [dateRange, viewRef]);

  const triggerChangeMenuItem = useCallback((menuItem) => {
    if (headerState && _.isFunction(headerState.setHidden)) {
      headerState.setHidden(!(menuItem && menuItem.length));
    }
  }, [headerState]);

  if (!headerState) {
    return null;
  }

  return (
    <div className={classNames('conversion-tracking-card', classes.cardContent, { [classes.smallSizeCard]: hideRangeColumn })}>
      <ConversionTrackingCardReact
        ref={viewRef}
        newI18n={i18n}
        i18n={legacyI18n}
        deps={deps}
        getCardsConfig={getCardsConfig}
        dateRange={dateRange}
        permissions={permissions}
        entitySet={entitySet}
        triggerChangeMenuItem={triggerChangeMenuItem}
        fetch={conversionTileFetch({
          entitySet,
          filter,
          campaignType,
          jsdata,
          getCardsConfig,
          dateUtil,
          permissions,
          cacheManager,
          typeId: cardTypeId,
        })}
        willFetchData={() => {
          willFetchData();
        }}
        renderedWithData={() => {
          willRender();
          done();
        }}
        TrackingTypeFilterStore={(errorType) => {
          if (errorType) {
            const newErrorTypeFilter = {
              TrackingStatus: {
                $in: [`Enum.GoalTrackingStatus'${errorType}'`],
              },
            };

            conversionGoalsFilterStore.save({ query: newErrorTypeFilter });
          }
        }}
        GoalCategoryFilterStore={(category) => {
          if (category) {
            const newCategoryFilter = {
              GoalCategory: {
                $in: [`Enum.GoalCategory'${category}'`],
              },
            };

            conversionGoalsFilterStore.save({ query: newCategoryFilter });
          }
        }}
      />
    </div>
  );
}

ConversionTrackingCardFluentInternal.propTypes = {
  i18n: PropTypes.shape({
    getString: PropTypes.func.isRequired,
  }).isRequired,
  perfMarkerUtils: PropTypes.shape({
    willFetchData: PropTypes.func.isRequired,
    willRender: PropTypes.func.isRequired,
    done: PropTypes.func.isRequired,
  }).isRequired,
  getCardsConfig: PropTypes.func.isRequired,
  permissions: PropTypes.shape({}).isRequired,
  preferenceService: PropTypes.shape({}).isRequired,
  deps: PropTypes.shape({
    constants: PropTypes.shape({}).isRequired,
    instrumentation: PropTypes.shape({}).isRequired,
  }).isRequired,
  jsdata: PropTypes.shape({}).isRequired,
  dateUtil: PropTypes.shape({}).isRequired,
  dateRange: PropTypes.shape({
    RangeStartDate: PropTypes.string,
    RangeEndDate: PropTypes.string,
    RangeId: PropTypes.number,
    Rangelcid: PropTypes.number,
    compared: PropTypes.shape({}),
    start: PropTypes.objectOf(Date),
    end: PropTypes.objectOf(Date),
    grain: PropTypes.number,
  }).isRequired,
  filter: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
  campaignType: PropTypes.oneOfType([
    PropTypes.number,
    PropTypes.string,
  ]).isRequired,
  constructOptions: PropTypes.func.isRequired,
  cacheManager: PropTypes.shape({}).isRequired,
  legacyI18n: PropTypes.shape({}).isRequired,
  cardTypeId: PropTypes.string.isRequired,
  responsiveSize: PropTypes.string.isRequired,
};

export const ConversionTrackingCardFluent = withDelayedStyles(ConversionTrackingCardFluentInternal, getDefaultStyles);
